import type { AssociatedTransferTransports } from '@bandido/shared/types';
import type {
  RuteroBookingLeg,
  RuteroBookingLegDirection,
  RuteroBookingLegEffectiveFlight,
} from '@bandido/shared/types';
import {
  effectiveRuteroBookingLegFlight,
  ruteroBookingLegs,
  ruteroMeetingPointText,
  sortRuteroBookingLegsByDirection,
} from '@bandido/shared/types';

import { transferPickupView } from './transferPickup';
import type { TransferPickupView } from './transferPickup';

/**
 * Los TRAMOS del traslado en pantalla (`booking.legs` del hub, solo
 * transfers): punto de encuentro, categoría del vehículo y hora de recogida
 * con su estado, por tramo (roundtrip = 2).
 *
 * Todo sale del registro AUTORITATIVO de la reserva (§21.23): la fila que
 * devuelven el hold, el confirm, el sync y las lecturas — nunca de la oferta
 * pre-hold ni del resumen congelado del formulario (`heldTripSummary`). Y lo
 * que se valida es lo que se dibuja: la lectura es `ruteroBookingLegs`, la
 * MISMA definición de "tramo válido" con la que el backend ingiere (§21.22).
 */

/**
 * La recogida de un tramo, ya decidida para dibujar.
 *
 * Es `TransferPickupView`: el hub publica el MISMO bloque en la oferta y en
 * el booking, así que la normalización es una sola (`transferPickup.ts`) y la
 * comparte la tarjeta de resultados. El alias se mantiene porque en este lado
 * el tramo es de una RESERVA y así se lee mejor en el render.
 */
export type BookingLegPickupView = TransferPickupView;

export interface BookingLegView {
  /** El id opaco del tramo: identidad estable para la lista. */
  key: string;
  direction: RuteroBookingLegDirection;
  /**
   * El tramo está cancelado (su `state` por tramo). Sirve para distinguir la
   * ida viva de la vuelta cancelada en un roundtrip cancelado a medias;
   * para el estado de la RESERVA manda `booking.state`.
   */
  cancelled: boolean;
  pickup?: BookingLegPickupView;
  meetingPointName?: string;
  /** Prosa del operador: se muestra escapada, nunca se interpreta. */
  meetingPointDescription?: string;
  instructionsUnavailable?: boolean;
  vehicleCategory?: string;
  associatedFlight?: RuteroBookingLegEffectiveFlight;
  associatedTransports?: AssociatedTransferTransports;
  /** Límite autoritativo de ESTE tramo, hora local de pared. */
  freeCancellationUntil?: string;
}

/**
 * `pickup.status` solo describe la disponibilidad de la HORA. La tranquilidad
 * comercial se muestra únicamente cuando la reserva y ese tramo siguen
 * confirmados; un hold o una pata cancelada no permiten afirmarla.
 */
export function bookingLegPickupReassuranceVisible(
  bookingState: unknown,
  legCancelled: boolean,
): boolean {
  return bookingState === 'confirmed' && !legCancelled;
}

function pickupView(leg: RuteroBookingLeg): BookingLegPickupView | undefined {
  // La zona del punto de partida (`start.timeZone`), que es donde recoge el
  // conductor: rotula la hora local. Ausente = el hub no la sabe y no se
  // asume ninguna. La oferta no la publica, por eso la pasa quien la tiene.
  return transferPickupView(leg.pickup, leg.start?.timeZone);
}

/**
 * Los tramos que la sección dibuja, o `null` si no hay sección. Data-driven:
 * un booking sin `legs` (otros verticales, proveedor sin detalle, backend
 * anterior al campo) no dibuja nada y no rompe nada.
 *
 * Un tramo sin NADA que mostrar (sin recogida, sin encuentro, sin vehículo y
 * vivo) se omite; si ningún tramo tiene contenido, la sección entera se omite
 * — campos ausentes son fila/sección ausente, nunca una fila vacía.
 */
export function bookingLegViews(
  legs: unknown,
  passengers?: readonly {
    flightNumber?: unknown;
    returnFlightNumber?: unknown;
    isContactPerson?: unknown;
  }[],
): BookingLegView[] | null {
  const reading = ruteroBookingLegs(legs);
  if (reading.status !== 'resolved') return null;
  const views = sortRuteroBookingLegsByDirection(reading.legs).flatMap((leg): BookingLegView[] => {
    const pickup = pickupView(leg);
    const associatedFlight = effectiveRuteroBookingLegFlight(leg, passengers);
    const cancelled = leg.state === 'cancelled';
    const view: BookingLegView = {
      key: leg.id,
      direction: leg.direction,
      cancelled,
      ...(pickup ? { pickup } : {}),
      ...(leg.meetingPoint?.name
        ? { meetingPointName: leg.meetingPoint.name }
        : {}),
      ...(ruteroMeetingPointText(leg.meetingPoint)
        ? { meetingPointDescription: ruteroMeetingPointText(leg.meetingPoint) }
        : {}),
      ...(leg.meetingPoint?.instructions?.status === 'exceeds_limit' ? { instructionsUnavailable: true } : {}),
      ...(leg.vehicleCategory ? { vehicleCategory: leg.vehicleCategory } : {}),
      ...(associatedFlight ? { associatedFlight } : {}),
      ...(leg.associatedTransports ? { associatedTransports: leg.associatedTransports } : {}),
      ...(leg.freeCancellationUntil
        ? { freeCancellationUntil: leg.freeCancellationUntil }
        : {}),
    };
    const hasContent =
      Boolean(pickup) ||
      Boolean(view.meetingPointName) ||
      Boolean(view.meetingPointDescription) ||
      Boolean(view.instructionsUnavailable) ||
      Boolean(view.vehicleCategory) ||
      Boolean(view.associatedFlight) ||
      Boolean(view.associatedTransports?.origin || view.associatedTransports?.destination) ||
      Boolean(view.freeCancellationUntil) ||
      cancelled;
    return hasContent ? [view] : [];
  });
  return views.length > 0 ? views : null;
}
